// AI Chat — SmartBiz Analytics Assistant.
//
// Ported from the old Harmony chat agent (recovered into
// smartbiz-toolkit-ui/chat-test-code/AiChat.jsx for reference — see
// .kiro/steering/ai-chat-tracker.md decision #10). Per that decision, this
// is a MINIMAL port, not a redesign: the event-driven streaming UI
// (markdown rendering, <NQS> quick-replies, thinking indicator, sidebar)
// is kept intact. What changed:
//
//   - userId is no longer hardcoded — comes from useAuth().username, the
//     server-verified Cognito identity (never trusted from the client).
//   - The WebSocket URL now carries the id_token as a `?token=` query
//     param (WS handshakes can't carry custom headers) and points at the
//     new backend in ap-south-1, not the old unauthenticated us-west-2
//     black box.
//   - New: knowledge_proposal / citation event handling for the
//     self-evolving knowledge base (tracker section "Self-evolving
//     knowledge base"). A proposal renders as its own bubble with
//     Approve/Reject. A citation renders as a small chip row under the
//     message it belongs to; clicking a chip opens the lesson in a modal
//     with an admin-only delete button.
//
// Wire protocol (matches smartbiz-toolkit/tools/ai-chat/lambda/handler.py
// + services/agent.py exactly):
//   Outbound actions: sendMessage, getConversations, getConversationHistory,
//                      approveKnowledge, rejectKnowledge
//   Inbound events: session_created, start, assistant_message_start, text,
//                    tool_call, tool_result, assistant_message_end, error,
//                    knowledge_proposal, knowledge_saved, knowledge_rejected,
//                    citation, conversation_list, conversation_history,
//                    dashboard_draft_update (4 Aug 2026 — Dashboards feature,
//                    see .kiro/steering/ai-chat-dashboard-tracker.md. Fired
//                    by the update_dashboard_draft tool; carries the full
//                    live widget list with real query results so the user
//                    watches the dashboard take shape turn by turn. The
//                    draft lives in frontend state only until "Save
//                    Dashboard" is clicked — see DashboardDraftPreview.jsx.)

import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { AI_CHAT_WS_URL } from "./aiChatApi.js";
import { dashboardsApi } from "./dashboardsApi.js";
import KnowledgeDetailModal from "./KnowledgeDetailModal.jsx";
import DashboardDraftPreview from "./DashboardDraftPreview.jsx";
import "./ai-chat.css";

/* ---------- markdownToHtml ----------
   Rewritten 30 Jul 2026 (presentation pass) — the old version was a flat
   regex pipeline that only understood headings/bold/italic/code and
   turned everything else (including markdown tables and bullet lists)
   into plain text with <br/> line breaks. That's exactly why a Claude
   response with a real markdown table rendered as a wall of pipe-
   delimited text instead of an actual table, and "- **label**: text"
   bullets kept their literal dash instead of becoming real <li> bullets.

   Now a lightweight line-by-line block parser: splits the text into
   blocks (table / list / heading / paragraph) the same way a minimal
   markdown renderer would, so tables and lists get real HTML structure.
   Inline formatting (bold/italic) is applied per-block via inlineMd().
   Kept deliberately simple (no external markdown lib) to match the
   existing hand-rolled-parser style and avoid adding a new dependency
   for what's a fairly constrained input shape (Claude's own markdown
   output, not arbitrary user-authored markdown). */

const inlineMd = (str) => {
  let out = str;
  out = out.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/\*(.*?)\*/g, "<em>$1</em>");
  return out;
};

const isTableRow = (line) => {
  const t = line.trim();
  return t.length > 1 && t.startsWith("|") && t.endsWith("|");
};

const isTableSeparatorRow = (line) => {
  if (!isTableRow(line)) return false;
  const cells = line.trim().slice(1, -1).split("|");
  return cells.length > 0 && cells.every((c) => /^\s*:?-{2,}:?\s*$/.test(c));
};

const splitTableRow = (line) => line.trim().slice(1, -1).split("|").map((c) => c.trim());

const renderMarkdownTable = (tableLines) => {
  const headerCells = splitTableRow(tableLines[0]);
  const bodyRows = tableLines.slice(2).map(splitTableRow);
  const thead = `<thead><tr>${headerCells.map((c) => `<th>${inlineMd(c)}</th>`).join("")}</tr></thead>`;
  const tbody = `<tbody>${bodyRows
    .map((row) => `<tr>${row.map((c) => `<td>${inlineMd(c)}</td>`).join("")}</tr>`)
    .join("")}</tbody>`;
  return `<div class="md-table-wrapper"><table class="md-table">${thead}${tbody}</table></div>`;
};

const BULLET_RE = /^[-*]\s+(.*)$/;
const ORDERED_RE = /^\d+[.)]\s+(.*)$/;

const markdownToHtml = (text) => {
  // Fenced code blocks pulled out first (may span multiple lines) so
  // nothing inside gets treated as table/list/heading syntax.
  const codeBlocks = [];
  let source = text.replace(/```([\s\S]*?)```/gim, (match, code) => {
    const id = codeBlocks.length;
    codeBlocks.push(code);
    return `__CODE_BLOCK_${id}__`;
  });

  const lines = source.split("\n");
  const parts = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Markdown table: a row followed by a |---|---| separator row.
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparatorRow(lines[i + 1])) {
      const tableLines = [line, lines[i + 1]];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j])) {
        tableLines.push(lines[j]);
        j++;
      }
      parts.push(renderMarkdownTable(tableLines));
      i = j;
      continue;
    }

    // Bullet or numbered list: consecutive lines of the same marker type.
    // Tolerates blank lines between items (common in LLM output).
    const bulletMatch = line.trim().match(BULLET_RE);
    const orderedMatch = !bulletMatch && line.trim().match(ORDERED_RE);
    if (bulletMatch || orderedMatch) {
      const tag = bulletMatch ? "ul" : "ol";
      const re = bulletMatch ? BULLET_RE : ORDERED_RE;
      const items = [];
      let j = i;
      while (j < lines.length) {
        const m = lines[j].trim().match(re);
        if (m) {
          items.push(m[1]);
          j++;
        } else if (lines[j].trim() === "" && j + 1 < lines.length && lines[j + 1].trim().match(re)) {
          // Skip blank line between list items
          j++;
        } else {
          break;
        }
      }
      parts.push(`<${tag}>${items.map((it) => `<li>${inlineMd(it)}</li>`).join("")}</${tag}>`);
      i = j;
      continue;
    }

    // Headings.
    const h4 = line.match(/^\s*###\s+(.*)$/);
    const h3 = !h4 && line.match(/^\s*##\s+(.*)$/);
    const h2 = !h4 && !h3 && line.match(/^\s*#\s+(.*)$/);
    if (h4) { parts.push(`<h4>${inlineMd(h4[1])}</h4>`); i++; continue; }
    if (h3) { parts.push(`<h3>${inlineMd(h3[1])}</h3>`); i++; continue; }
    if (h2) { parts.push(`<h2>${inlineMd(h2[1])}</h2>`); i++; continue; }

    // A code-block placeholder occupies its own line with no surrounding
    // <br/> — the real <pre> substitution below adds its own spacing.
    if (/^__CODE_BLOCK_\d+__$/.test(line.trim())) {
      parts.push(line.trim());
      i++;
      continue;
    }

    // Blank source line = paragraph gap; a single trailing <br/> here,
    // combined with the previous line's own trailing <br/>, reproduces
    // the old "double line break between paragraphs" spacing.
    if (line.trim() === "") {
      parts.push("<br/>");
      i++;
      continue;
    }

    // Plain text line.
    parts.push(`${inlineMd(line)}<br/>`);
    i++;
  }

  let html = parts.join("");

  codeBlocks.forEach((code, idx) => {
    const safe = code.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    html = html.replace(`__CODE_BLOCK_${idx}__`, `<pre><code>${safe}</code></pre>`);
  });

  html = html.replace(/(<br\/>\s*){3,}/g, "<br/><br/>");
  html = html.replace(/^(<br\/>)+|(<br\/>)+$/g, "");
  return html;
};

function ChatThemeToggle() {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('smartbiz-theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (isDark) {
      root.setAttribute('data-theme', 'dark');
      body.classList.add('awsui-dark-mode');
    } else {
      root.setAttribute('data-theme', 'light');
      body.classList.remove('awsui-dark-mode');
    }
    localStorage.setItem('smartbiz-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <button
      className="chat-theme-toggle"
      onClick={() => setIsDark((d) => !d)}
      aria-label="Toggle light/dark theme"
    >
      {isDark ? '🌙' : '☀️'}
    </button>
  );
}

const CHAT_PAGE_SIZE = 15;
const DASHBOARD_PAGE_SIZE = 8;

export default function AiChat() {
  const { idToken, isAdmin, isInternal } = useAuth();

  /* -------------------------- STATE -------------------------- */
  const [messages, setMessages] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [scrollGrown, setScrollGrown] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [connectionError, setConnectionError] = useState("");

  const [conversationList, setConversationList] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  const [knowledgeModalId, setKnowledgeModalId] = useState(null);

  // Dashboards sidebar section (4 Aug 2026). Fetched via the separate
  // HTTP dashboardsApi, not the WS connection — CRUD/list is a plain
  // request/response concern (locked decision #8), the WS is only used
  // for the conversational BUILD flow.
  const [ownedDashboards, setOwnedDashboards] = useState([]);
  const [sharedDashboards, setSharedDashboards] = useState([]);
  const [chatDisplayCount, setChatDisplayCount] = useState(CHAT_PAGE_SIZE);
  const [dashDisplayCount, setDashDisplayCount] = useState(DASHBOARD_PAGE_SIZE);
  const [attachments, setAttachments] = useState([]); // [{name, type, data (base64)}]
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer state

  /* -------------------------- REFS -------------------------- */
  const wsRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  const agentIndexRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  // Tracks the message index of the CURRENT dashboard-build preview
  // bubble, if any. update_dashboard_draft replaces the ENTIRE draft each
  // call (per the tool's own contract), so repeated calls within one
  // build session update this one bubble in place rather than stacking a
  // new bubble per iteration. Reset to null whenever a new user message
  // is sent or a new chat starts, so a later, unrelated dashboard-build
  // conversation starts its own fresh bubble.
  const dashboardDraftIndexRef = useRef(null);
  // Timer for measuring response time — set to Date.now() when the user
  // sends a message, read on assistant_message_end to compute elapsed.
  const responseStartRef = useRef(null);

  /* -------------------------- EFFECTS -------------------------- */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${Math.min(
      textareaRef.current.scrollHeight,
      300
    )}px`;
  }, [input]);

  useEffect(() => {
    const chatBox = messagesEndRef.current?.parentElement;
    if (chatBox) {
      const height = chatBox.getBoundingClientRect().height;
      setScrollGrown(height > window.innerHeight * 0.4);
    }
  }, [messages]);

  useEffect(() => {
    if (idToken) connectWebSocket();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
    // Only reconnect if the token itself changes (e.g. token refresh) — not
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]);

  const loadDashboards = () => {
    dashboardsApi.listDashboards()
      .then((data) => {
        setOwnedDashboards(data.owned || []);
        setSharedDashboards(data.shared_with_me || []);
      })
      .catch(() => { /* sidebar section just stays empty on failure — non-critical */ });
  };

  useEffect(() => {
    if (idToken) loadDashboards();
  }, [idToken]);

  /* -------------------------- HELPERS -------------------------- */
  const createAgentBubble = () => {
    let newIndex;
    setMessages((prev) => {
      const copy = [...prev];
      newIndex = copy.length;
      copy.push({ sender: "agent", text: "", metaText: [], citations: [] });
      return copy;
    });
    agentIndexRef.current = newIndex;
    return newIndex;
  };

  const appendMetaMessage = (chunk) => {
    if (agentIndexRef.current == null) createAgentBubble();
    setMessages((prev) => {
      const copy = [...prev];
      const idx = agentIndexRef.current;
      if (idx == null) return copy;
      const existing = copy[idx].metaText || [];
      // A multi-round tool-use turn (e.g. "find the shop ID" then "get
      // the sales data") emits the identical "Running run_redshift_query…"
      // step line once per round. Rather than stacking up N visually
      // identical lines, collapse consecutive duplicates into one line
      // with a small "×N" counter so it's clear multiple queries ran
      // without repeating the same sentence.
      const last = existing[existing.length - 1];
      if (last && last.text === chunk) {
        const updated = [...existing];
        updated[updated.length - 1] = { text: chunk, count: last.count + 1 };
        copy[idx] = { ...copy[idx], metaText: updated };
      } else {
        copy[idx] = { ...copy[idx], metaText: [...existing, { text: chunk, count: 1 }] };
      }
      return copy;
    });
  };

  const appendToAgentBubble = (chunk) => {
    if (agentIndexRef.current === null) createAgentBubble();
    setMessages((prev) => {
      const copy = [...prev];
      const idx = agentIndexRef.current;
      if (idx == null) return copy;
      copy[idx] = { ...copy[idx], text: (copy[idx].text || "") + chunk };
      return copy;
    });
  };

  const setCitationsOnAgentBubble = (knowledgeIds) => {
    if (agentIndexRef.current === null) return;
    setMessages((prev) => {
      const copy = [...prev];
      const idx = agentIndexRef.current;
      if (idx == null) return copy;
      copy[idx] = {
        ...copy[idx],
        citations: [...new Set([...(copy[idx].citations || []), ...knowledgeIds])],
      };
      return copy;
    });
  };

  const finalizeAgentBubble = (responseTime) => {
    if (responseTime != null && agentIndexRef.current !== null) {
      setMessages((prev) => {
        const copy = [...prev];
        const idx = agentIndexRef.current;
        if (copy[idx]) copy[idx] = { ...copy[idx], responseTime };
        return copy;
      });
    }
    agentIndexRef.current = null;
  };

  const processVisibleText = (chunk) => {
    if (!chunk) return;
    appendToAgentBubble(chunk);
  };

  const addKnowledgeProposal = (proposal) => {
    setMessages((prev) => [
      ...prev,
      {
        sender: "knowledge_proposal",
        proposalId: proposal.proposal_id,
        content: proposal.content,
        category: proposal.category,
        decided: null, // null | "approved" | "rejected"
      },
    ]);
  };

  const markProposalDecided = (proposalId, decision) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.sender === "knowledge_proposal" && m.proposalId === proposalId
          ? { ...m, decided: decision }
          : m
      )
    );
  };

  const upsertDashboardDraft = (title, widgets, { saved, dashboard_id, is_edit } = {}) => {
    setMessages((prev) => {
      const copy = [...prev];
      const idx = dashboardDraftIndexRef.current;
      const draftObj = { title, widgets, ...(dashboard_id ? { dashboard_id } : {}), ...(is_edit ? { is_edit: true } : {}), ...(saved ? { saved: true } : {}) };
      if (idx != null && copy[idx]?.sender === "dashboard_draft") {
        copy[idx] = { ...copy[idx], draft: draftObj };
        return copy;
      }
      dashboardDraftIndexRef.current = copy.length;
      copy.push({ sender: "dashboard_draft", draft: draftObj });
      return copy;
    });
  };

  /* -------------------------- WEBSOCKET HANDLER -------------------------- */
  const connectWebSocket = () => {
    if (!idToken) return;
    try {
      const socket = new WebSocket(`${AI_CHAT_WS_URL}?token=${encodeURIComponent(idToken)}`);
      wsRef.current = socket;

      socket.onopen = () => {
        setConnectionError("");
        // Populate the sidebar's "Your Chats" list. The old Harmony
        // version left this commented out ("disable sidebar loading
        // unless slow loading is resolved") but kept the sidebar UI —
        // this port wires it up since handler.py's
        // _handle_get_conversations is real and already deployed.
        socket.send(JSON.stringify({ action: "getConversations" }));
      };

      socket.onclose = () => {
        reconnectTimerRef.current = setTimeout(connectWebSocket, 1500);
      };

      socket.onerror = () => {
        setConnectionError("Connection issue — retrying…");
      };

      socket.onmessage = (event) => {
        let raw = String(event.data || "");
        if (!raw.trim()) return;
        if (raw.startsWith("data:")) raw = raw.replace(/^data:\s*/, "");
        if (raw.includes("Endpoint request timed out")) return;

        let parsed = null;
        try {
          const sanitizedRaw = raw
            .replace(/\bNaN\b/g, "null")
            .replace(/\bInfinity\b/g, "null")
            .replace(/\b-Infinity\b/g, "null");
          parsed = JSON.parse(sanitizedRaw);
        } catch {
          return;
        }

        if (parsed.event === "conversation_list") {
          setConversationList(parsed.sessions || []);
          return;
        }

        if (parsed.event === "conversation_history") {
          const mapped = parsed.history.map((msg) => {
            const sender = msg.role === "assistant" ? "agent" : "user";
            let content = msg.content;
            if (sender === "user") {
              content = content.replace(/^\[user_id=.*?\]\s*/, "").trim();
            }
            return { sender, text: content, citations: [], seq: msg.seq || null, feedback: msg.feedback || null };
          });
          setMessages(mapped);
          setSessionId(parsed.session_id);
          setSelectedSession(parsed.session_id);
          setLoading(false);
          agentIndexRef.current = null;
          return;
        }

        const t = parsed.type;

        switch (t) {
          case "start":
            if (agentIndexRef.current == null) createAgentBubble();
            if (parsed.session_id) setSessionId(parsed.session_id);
            setLoading(true);
            if (parsed.message) appendMetaMessage(parsed.message);
            break;

          case "session_created":
            if (parsed.session_id) setSessionId(parsed.session_id);
            // Refresh the sidebar so the just-started chat shows up
            // without waiting for a reconnect.
            sendAction("getConversations");
            break;

          case "assistant_message_start":
            if (agentIndexRef.current == null) createAgentBubble();
            break;

          case "text":
            if (typeof parsed.content === "string") processVisibleText(parsed.content);
            break;

          case "tool_call":
            if (parsed.message) appendMetaMessage(parsed.message);
            break;

          case "tool_result": {
            const result = parsed.result;
            if (!result || !result.status) break;
            if (result.status === "error") {
              const errMsg = result.message || "Tool returned an unknown error.";
              appendToAgentBubble(`\n\n<strong style="color:red">Tool Error:</strong> ${errMsg}`);
            }
            break;
          }

          case "citation":
            if (Array.isArray(parsed.knowledge_ids) && parsed.knowledge_ids.length) {
              setCitationsOnAgentBubble(parsed.knowledge_ids);
            }
            break;

          case "dashboard_draft_update":
            if (parsed.title && Array.isArray(parsed.widgets)) {
              upsertDashboardDraft(parsed.title, parsed.widgets, {
                saved: parsed.saved || false,
                dashboard_id: parsed.dashboard_id || null,
                is_edit: parsed.is_edit || false,
              });
            }
            break;

          case "knowledge_proposal":
            addKnowledgeProposal(parsed);
            break;

          case "knowledge_saved":
            markProposalDecided(parsed.proposal_id, "approved");
            break;

          case "knowledge_rejected":
            markProposalDecided(parsed.proposal_id, "rejected");
            break;

          case "knowledge_pending_review":
            // A non-admin clicked Approve — the lesson stays pending,
            // awaiting a real admin review (see handler.py's
            // _handle_approve_knowledge). Distinct decided state so the
            // bubble can say something different from "Saved".
            markProposalDecided(parsed.proposal_id, "pending_review");
            break;

          case "feedback_saved":
            // Server confirmed the rating — no-op since we already did
            // the optimistic UI update in handleRateResponse.
            break;

          case "message_seq": {
            // Stamp the seq onto the most recent agent message so feedback
            // buttons know which message to rate. Uses a search from the
            // end since agentIndexRef may already be cleared by
            // assistant_message_end.
            const targetSeq = parsed.seq;
            if (targetSeq != null) {
              setMessages((prev) => {
                const copy = [...prev];
                // Find the last agent message without a seq
                for (let k = copy.length - 1; k >= 0; k--) {
                  if (copy[k].sender === "agent" && copy[k].seq == null) {
                    copy[k] = { ...copy[k], seq: targetSeq };
                    break;
                  }
                }
                return copy;
              });
            }
            break;
          }

          case "assistant_saved":
            setLoading(false);
            break;

          case "assistant_message_end": {
            const currentAgentIdx = agentIndexRef.current;
            const elapsed = responseStartRef.current
              ? Math.round((Date.now() - responseStartRef.current) / 1000)
              : null;
            responseStartRef.current = null;
            setLoading(false);
            // Stamp responseTime directly on the message at the known index,
            // then clear agentIndexRef — this avoids the race where
            // agentIndexRef gets cleared by a re-render between setLoading
            // and finalizeAgentBubble.
            if (elapsed != null && currentAgentIdx != null) {
              setMessages((prev) => {
                const copy = [...prev];
                if (copy[currentAgentIdx]) copy[currentAgentIdx] = { ...copy[currentAgentIdx], responseTime: elapsed };
                return copy;
              });
            }
            agentIndexRef.current = null;
            break;
          }

          case "error":
            setLoading(false);
            appendToAgentBubble(`\n\n<strong style="color:red">Error:</strong> ${parsed.message}`);
            finalizeAgentBubble();
            break;

          case "turn_cancelled":
            // Server acknowledged the cancel — nothing to do (loading
            // already set to false by handleStop; the worker will finish
            // and send assistant_message_end once it exits its loop).
            break;

          default:
            if (parsed.message) appendToAgentBubble(parsed.message);
        }
      };
    } catch (err) {
      setConnectionError("Could not connect. Retrying…");
      reconnectTimerRef.current = setTimeout(connectWebSocket, 1500);
    }
  };

  /* -------------------------- SEND MESSAGE -------------------------- */
  const sendViaWebSocket = (text) => {
    if (!text.trim()) return;

    // If WS isn't connected, queue a retry — but DON'T append the user
    // message to state yet. Only append once we actually send. This
    // prevents the old bug where each reconnect retry added another
    // duplicate copy of the same user message to the chat.
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connectWebSocket();
      setTimeout(() => sendViaWebSocket(text), 500);
      return;
    }

    setMessages((prev) => [...prev, { sender: "user", text, citations: [] }]);
    setShowWelcome(false);
    setLoading(true);
    agentIndexRef.current = null;
    responseStartRef.current = Date.now();
    // A new user message starts a fresh dashboard-build bubble if this
    // turn happens to touch update_dashboard_draft again — don't keep
    // overwriting whatever bubble was last shown several turns ago.
    dashboardDraftIndexRef.current = null;
    const payload = {
      action: "sendMessage",
      input: {
        prompt: text,
        ...(sessionId ? { session_id: sessionId } : {}),
        ...(attachments.length > 0 ? { attachments: attachments.map((a) => ({ name: a.name, type: a.type, key: a.key })) } : {}),
      },
    };
    wsRef.current.send(JSON.stringify(payload));
    setInput("");
    setAttachments([]);
  };

  const sendAction = (action, extra = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ action, ...extra }));
  };

  const handleApproveKnowledge = (proposalId) => {
    sendAction("approveKnowledge", { proposal_id: proposalId });
    // Optimistic UI — server confirms via knowledge_saved (admins, goes
    // live immediately) or knowledge_pending_review (non-admins, stays
    // pending for a real admin to review — see handler.py). Guess the
    // right optimistic state from isAdmin so a non-admin doesn't briefly
    // see "Saved" before the correction arrives.
    markProposalDecided(proposalId, isAdmin ? "approved" : "pending_review");
  };

  const handleRejectKnowledge = (proposalId) => {
    sendAction("rejectKnowledge", { proposal_id: proposalId });
    markProposalDecided(proposalId, "rejected");
  };

  const handleRateResponse = (sessionId, seq, rating, comment, messageIndex) => {
    sendAction("rateResponse", { session_id: sessionId, seq, rating, comment });
    // Optimistic UI update
    setMessages((prev) => {
      const updated = [...prev];
      if (updated[messageIndex]) {
        updated[messageIndex] = { ...updated[messageIndex], feedback: rating };
      }
      return updated;
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!loading) sendViaWebSocket(input);
    }
  };

  // Stop/cancel — sends a cancelTurn action to the backend, which sets a
  // flag that makes the in-flight worker exit at the next tool-round check.
  // Keeps the WS connection alive (no reconnect needed). The worker will
  // stop within one tool round (~5-10s) and send a "(Stopped)" text event.
  const handleStop = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: "cancelTurn",
        input: { session_id: sessionId },
      }));
    }
    setLoading(false);
    responseStartRef.current = null;
  };

  // File attachment handler — reads the file as base64 and adds it to
  // the attachments list. Supported: images (PNG/JPEG/GIF/WebP), PDFs,
  // CSVs (sent as text). Max 5MB per file (Bedrock's document limit).
  const SUPPORTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', 'text/csv'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so re-selecting same file triggers again

    if (file.size > MAX_FILE_SIZE) {
      alert(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
      return;
    }

    // Step 1: Get a presigned upload URL from the dashboards API
    try {
      const { apiFetch } = await import('../../lib/apiFetch.js');
      const { API_HOSTS } = await import('../../config.js');
      const resp = await apiFetch(`${API_HOSTS.aiChatDashboards}/api/chat-attachments/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'application/octet-stream' }),
      });
      if (!resp.ok) { alert('Failed to get upload URL.'); return; }
      const { upload_url, key } = await resp.json();

      // Step 2: PUT the raw file bytes directly to S3
      const putResp = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putResp.ok) { alert('Failed to upload file.'); return; }

      // Step 3: Store the S3 key (not the file data) in state
      setAttachments((prev) => [...prev, { name: file.name, type: file.type || 'application/octet-stream', key }]);
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const startNewChat = () => {
    setMessages([]);
    setShowWelcome(true);
    setSessionId(null);
    setSelectedSession(null);
    dashboardDraftIndexRef.current = null;
  };

  // Suggestion cards shown on the welcome screen — clicking one sends
  // that text as the first message, same as typing it manually.
  const WELCOME_SUGGESTIONS = [
    { icon: "📈", title: "Weekly channel-wise sales, last 10 weeks", sub: "Trend across ASSR, Partner, Direct & more" },
    { icon: "📉", title: "Shops where sales decreased in the last 4 weeks vs prior 4 weeks", sub: "Identify stores losing momentum" },
    { icon: "🗂️", title: "What tables can you query?", sub: "Browse the warehouse schema" },
    { icon: "🚀", title: "New stores launched this month with first-week GMS > 10K", sub: "Spot high-potential recent launches" },
  ];

  /* -------------------------- UI -------------------------- */
  return (
    <div className="ai-chat-layout">
      {/* Overlay behind sidebar on mobile — click to close */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? "sidebar-open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />
      {/* ═══════════════════════════════════════════════════════════
          SIDEBAR — slides in on mobile, always visible on desktop.
          ═══════════════════════════════════════════════════════════ */}
      <div className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        {/* Top action — new chat */}
        <button className="sidebar-new-chat-btn" onClick={() => { startNewChat(); setSidebarOpen(false); }}>
          + New chat
        </button>

        {/* Knowledge base link — moved here from the main area's top-right */}
        {isInternal && (
          <Link to="/ai-chat/knowledge" className="sidebar-knowledge-link">
            🧠 Knowledge base
          </Link>
        )}

        {/* Chats section */}
        <div className="sidebar-section">
          <div className="sidebar-section-header">
            <span className="sidebar-section-label">Chats</span>
            <span className="sidebar-section-badge">{conversationList.length}</span>
          </div>
          <div className="sidebar-section-items sidebar-section-scroll">
            {conversationList.length === 0 && (
              <div className="sidebar-item empty">No conversations</div>
            )}
            {conversationList.slice(0, chatDisplayCount).map((c) => (
              <div
                key={c.sessionId}
                className={`sidebar-item ${selectedSession === c.sessionId ? "active" : ""}`}
                onClick={() => {
                  setSelectedSession(c.sessionId);
                  setMessages([]);
                  setShowWelcome(false);
                  setLoading(true);
                  sendAction("getConversationHistory", { session_id: c.sessionId });
                  setSidebarOpen(false); // close drawer on mobile
                }}
              >
                <span className="sidebar-item-title">
                  💬 {(c.title || "New Conversation").slice(0, 150)}
                </span>
                {c.createdAt && (
                  <span className="sidebar-item-date">
                    {new Date(c.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
            ))}
            {conversationList.length > chatDisplayCount && (
              <button
                className="sidebar-show-more"
                onClick={() => setChatDisplayCount((n) => n + CHAT_PAGE_SIZE)}
              >
                Show older ({conversationList.length - chatDisplayCount} more)
              </button>
            )}
          </div>
        </div>

        {/* Dashboards section */}
        <div className="sidebar-section sidebar-section-dashboards">
          <div className="sidebar-section-header">
            <span className="sidebar-section-label">Dashboards</span>
            <span className="sidebar-section-badge">{ownedDashboards.length + sharedDashboards.length}</span>
          </div>
          <div className="sidebar-section-items sidebar-section-scroll">
            {ownedDashboards.length === 0 && sharedDashboards.length === 0 && (
              <div className="sidebar-item empty">No dashboards yet</div>
            )}
            {ownedDashboards.slice(0, dashDisplayCount).map((d) => {
              const sharedWith = d.shared_with || [];
              const visibleShares = sharedWith.slice(0, 3);
              const moreCount = sharedWith.length - visibleShares.length;
              return (
                <a
                  key={d.dashboard_id}
                  className="sidebar-item sidebar-dashboard-item"
                  href={`/ai-chat/dashboard/${d.dashboard_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="sidebar-dashboard-title">📊 {d.title}</span>
                  <span className="sidebar-dashboard-meta">
                    <span className="sidebar-dashboard-chip owned">owned</span>
                    {visibleShares.map((u) => (
                      <span key={u} className="sidebar-dashboard-chip shared-user">{u}</span>
                    ))}
                    {moreCount > 0 && (
                      <span className="sidebar-dashboard-chip shared-user">+{moreCount}</span>
                    )}
                  </span>
                  {sharedWith.length > 0 && (
                    <span className="sidebar-dashboard-hover-tooltip">
                      <span className="sidebar-dashboard-tooltip-label">Shared with:</span>
                      <span className="sidebar-dashboard-tooltip-chips">
                        {sharedWith.map((u) => (
                          <span key={u} className="sidebar-dashboard-tooltip-chip">{u}</span>
                        ))}
                      </span>
                    </span>
                  )}
                </a>
              );
            })}
            {sharedDashboards.slice(0, Math.max(0, dashDisplayCount - ownedDashboards.length)).map((d) => (
              <a
                key={d.dashboard_id}
                className="sidebar-item sidebar-dashboard-item"
                href={`/ai-chat/dashboard/${d.dashboard_id}`}
                target="_blank"
                rel="noreferrer"
                title={`${d.widget_count} widgets · Shared by ${d.shared_by || d.owner_username}`}
              >
                <span className="sidebar-dashboard-title">📊 {d.title}</span>
                <span className="sidebar-dashboard-chip shared">by {d.shared_by || d.owner_username}</span>
              </a>
            ))}
            {(ownedDashboards.length + sharedDashboards.length) > dashDisplayCount && (
              <button
                className="sidebar-show-more"
                onClick={() => setDashDisplayCount((n) => n + DASHBOARD_PAGE_SIZE)}
              >
                Show older ({ownedDashboards.length + sharedDashboards.length - dashDisplayCount} more)
              </button>
            )}
          </div>
        </div>

        {/* User info at the bottom */}
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">
            {(useAuth().username || "?")[0].toUpperCase()}
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{useAuth().username || "user"}</div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          MAIN CHAT AREA
          ═══════════════════════════════════════════════════════════ */}
      <div className="chat-main">
        {/* Top bar — connection status + hamburger (mobile) + theme toggle */}
        <div className="chat-topbar">
          <span className="chat-topbar-status">
            {connectionError
              ? <><span className="chat-topbar-dot disconnected" /> {connectionError}</>
              : <><span className="chat-topbar-dot connected" /> Connected</>
            }
          </span>
          <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
            <ChatThemeToggle />
            <button className="sidebar-mobile-toggle" onClick={() => setSidebarOpen((o) => !o)} aria-label="Toggle sidebar">
              ☰
            </button>
          </div>
        </div>

        {/* Messages / Welcome screen */}
        <div className="chat-messages-area">
          {showWelcome && messages.length === 0 ? (
            <div className="chat-welcome">
              <div className="chat-welcome-mark">S</div>
              <h1 className="chat-welcome-title">
                👋 Hi {useAuth().username || "there"}, I'm your Analytics Assistant
              </h1>
              <p className="chat-welcome-sub">
                I query the SmartBiz warehouse to surface trends, rank sellers,
                and spin up dashboards — just ask.
              </p>
              <div className="chat-welcome-cards">
                {WELCOME_SUGGESTIONS.map((s, idx) => (
                  <button
                    key={idx}
                    className="chat-welcome-card"
                    onClick={() => sendViaWebSocket(s.title)}
                  >
                    <span className="chat-welcome-card-icon">{s.icon}</span>
                    <span className="chat-welcome-card-text">
                      <strong>{s.title}</strong>
                      <span className="chat-welcome-card-sub">{s.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
          <div className="chat-box">
            {messages.map((m, i) => {
              if (m.sender === "knowledge_proposal") {
                return (
                  <KnowledgeProposalBubble
                    key={i}
                    message={m}
                    onApprove={() => handleApproveKnowledge(m.proposalId)}
                    onReject={() => handleRejectKnowledge(m.proposalId)}
                  />
                );
              }

              if (m.sender === "dashboard_draft") {
                return (
                  <DashboardDraftPreview
                    key={i}
                    draft={m.draft}
                    sessionId={sessionId}
                    onSaved={loadDashboards}
                  />
                );
              }

              const isLastAgent =
                i === messages.length - 1 && m.sender === "agent" && loading;

              return (
                <div key={i} className={`chat-message ${m.sender}`}>
                  <div className="agent-message-text" style={{ whiteSpace: "pre-wrap" }}>
                    {m.metaText?.length > 0 && (
                      <div className="meta-message-wrapper">
                        {m.metaText.map((meta, idx) => (
                          <div key={idx} className="meta-message">
                            {meta.text}
                            {meta.count > 1 && <span className="meta-message-count"> ×{meta.count}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(markdownToHtml(m.text)),
                      }}
                    />
                    {isLastAgent && <span className="typing-cursor chatgpt-cursor">█</span>}

                    {m.citations?.length > 0 && (
                      <div className="ai-chat-citations">
                        📎 Based on {m.citations.length} learned fact{m.citations.length > 1 ? "s" : ""}:
                        {m.citations.map((kid) => (
                          <button
                            key={kid}
                            className="ai-chat-citation-chip"
                            onClick={() => setKnowledgeModalId(kid)}
                          >
                            view
                          </button>
                        ))}
                      </div>
                    )}

                    {m.sender === "agent" && m.responseTime != null && (
                      <div className="chat-response-time">
                        Response time: {m.responseTime >= 60
                          ? `${Math.floor(m.responseTime / 60)} min ${m.responseTime % 60}s`
                          : `${m.responseTime}s`}
                      </div>
                    )}

                    {/* 👍/👎 feedback buttons — shown on completed agent messages */}
                    {m.sender === "agent" && !loading && m.seq != null && (
                      <FeedbackButtons
                        sessionId={sessionId}
                        seq={m.seq}
                        existingRating={m.feedback}
                        onRate={(rating, comment) => handleRateResponse(sessionId, m.seq, rating, comment, i)}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="chat-message agent thinking-block">
                <div className="thinking-spinner" />
                <span className="thinking-text">Thinking</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
          )}
        </div>

        {/* Input area — bottom-anchored, full-width */}
        <div className="chat-input-area">
          {/* Attachment preview chips */}
          {attachments.length > 0 && (
            <div className="chat-attachments">
              {attachments.map((a, idx) => (
                <span key={idx} className="chat-attachment-chip">
                  📎 {a.name}
                  <button className="chat-attachment-remove" onClick={() => removeAttachment(idx)}>×</button>
                </span>
              ))}
            </div>
          )}
          <div className="chat-input-container">
            <label className="chat-upload-btn" title="Attach a file (PDF, CSV, image — max 5 MB)">
              <input
                type="file"
                accept=".pdf,.csv,.png,.jpg,.jpeg,.gif,.webp"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.49" />
              </svg>
            </label>
            <textarea
              ref={textareaRef}
              placeholder="What would you like to explore today?"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              className="chat-input"
              rows={1}
            />
            {loading ? (
              <button
                className="chat-send-btn chat-stop-btn"
                onClick={handleStop}
                aria-label="Stop generating"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                className="chat-send-btn"
                onClick={() => sendViaWebSocket(input)}
                disabled={!input.trim()}
                aria-label="Send message"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            )}
          </div>
          <div className="chat-input-hints">
            <span>Enter to send · Shift+Enter for newline</span>
          </div>
        </div>
      </div>

      {knowledgeModalId && (
        <KnowledgeDetailModal
          knowledgeId={knowledgeModalId}
          isAdmin={isAdmin}
          onDismiss={() => setKnowledgeModalId(null)}
          onDeleted={() => setKnowledgeModalId(null)}
        />
      )}
    </div>
  );
}

function FeedbackButtons({ sessionId, seq, existingRating, onRate }) {
  const [rating, setRating] = useState(existingRating || null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");

  // Sync with prop if it changes (e.g. history reload)
  useEffect(() => {
    if (existingRating) setRating(existingRating);
  }, [existingRating]);

  const handleRate = (value) => {
    if (rating) return; // already rated
    if (value === "down") {
      setShowComment(true);
      setRating(value);
    } else {
      setRating(value);
      onRate(value, "");
    }
  };

  const submitDownvote = () => {
    onRate("down", comment);
    setShowComment(false);
  };

  return (
    <div className="ai-chat-feedback-buttons">
      <button
        className={`ai-chat-feedback-btn ${rating === "up" ? "active" : ""} ${rating && rating !== "up" ? "faded" : ""}`}
        onClick={() => handleRate("up")}
        disabled={!!rating}
        title="Good response"
      >
        👍
      </button>
      <button
        className={`ai-chat-feedback-btn ${rating === "down" ? "active" : ""} ${rating && rating !== "down" ? "faded" : ""}`}
        onClick={() => handleRate("down")}
        disabled={!!rating && !showComment}
        title="Bad response"
      >
        👎
      </button>
      {showComment && (
        <div className="ai-chat-feedback-comment">
          <input
            type="text"
            placeholder="What was wrong? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitDownvote(); }}
            autoFocus
          />
          <button onClick={submitDownvote} className="ai-chat-feedback-submit">Send</button>
        </div>
      )}
    </div>
  );
}

function KnowledgeProposalBubble({ message, onApprove, onReject }) {
  const { content, category, decided } = message;
  return (
    <div className="ai-chat-knowledge-proposal">
      <div className="ai-chat-knowledge-proposal-header">
        💡 I think I learned something
        <span className="ai-chat-knowledge-category">{category}</span>
      </div>
      <div className="ai-chat-knowledge-proposal-content">{content}</div>
      {decided === null && (
        <div className="ai-chat-knowledge-proposal-actions">
          <button className="ai-chat-knowledge-approve" onClick={onApprove}>✓ Save this</button>
          <button className="ai-chat-knowledge-reject" onClick={onReject}>✗ No, discard</button>
        </div>
      )}
      {decided === "approved" && (
        <div className="ai-chat-knowledge-decided approved">✓ Saved — I'll remember this going forward.</div>
      )}
      {decided === "rejected" && (
        <div className="ai-chat-knowledge-decided rejected">✗ Discarded.</div>
      )}
      {decided === "pending_review" && (
        <div className="ai-chat-knowledge-decided pending-review">
          👀 Noted — sent to an admin for review before it's used going forward.
        </div>
      )}
    </div>
  );
}
